
import { useEffect, useState, useRef } from "react";

/**
 * useSharedImageLoader
 * Preloads an array of image URLs and returns a loading flag that is false only when all images are loaded.
 * Adds support for caching loaded images in memory to improve subsequent loads.
 */
export function useSharedImageLoader(urls: string[]): boolean {
  const [loading, setLoading] = useState(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!urls.length) {
      setLoading(false);
      return;
    }

    // Check if we have these images in memory cache
    const cachedImages = window.__preloadedImages || {};
    window.__preloadedImages = cachedImages;

    // Sort URLs by priority - put platform logos first
    const prioritizedUrls = [...urls].sort((a, b) => {
      // Check for platform logos that should be loaded first
      const isPlatformLogoA = a.includes('logo_ctrader') || 
                            a.includes('fxpro') || 
                            a.includes('ftmo') || 
                            a.includes('ai_prop');
                            
      const isPlatformLogoB = b.includes('logo_ctrader') || 
                            b.includes('fxpro') || 
                            b.includes('ftmo') || 
                            b.includes('ai_prop');
      
      if (isPlatformLogoA && !isPlatformLogoB) return -1;
      if (!isPlatformLogoA && isPlatformLogoB) return 1;
      
      // Local assets should come before remote ones
      const isLocalA = a.startsWith('/');
      const isLocalB = b.startsWith('/');
      if (isLocalA && !isLocalB) return -1;
      if (!isLocalA && isLocalB) return 1;
      
      return 0;
    });
    
    // Check if all images are already cached
    let allCached = true;
    for (const url of urls) {
      if (!cachedImages[url]) {
        allCached = false;
        break;
      }
    }
    
    if (allCached) {
      setLoading(false);
      return;
    }
    
    let loaded = 0;
    const totalToLoad = prioritizedUrls.length;

    // Initialize the timer to provide a fallback in case images take too long
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }, 3000); // 3 seconds timeout as fallback

    const loadImage = (url: string) => {
      // Skip if already cached
      if (cachedImages[url]) {
        loaded++;
        if (isMountedRef.current && loaded === totalToLoad) {
          clearTimeout(timeoutId);
          setLoading(false);
        }
        return;
      }
      
      const img = new window.Image();
      img.onload = () => {
        loaded++;
        cachedImages[url] = true;
        if (isMountedRef.current && loaded === totalToLoad) {
          clearTimeout(timeoutId);
          setLoading(false);
        }
      };
      img.onerror = () => {
        loaded++;
        if (isMountedRef.current && loaded === totalToLoad) {
          clearTimeout(timeoutId);
          setLoading(false);
        }
      };
      img.src = url;
    };

    // Load images in order of priority
    prioritizedUrls.forEach(loadImage);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
    };
  }, [urls]);

  return loading;
}

// Add TypeScript global augmentation for our custom property
declare global {
  interface Window {
    __preloadedImages?: Record<string, boolean>;
  }
}
